<script setup>
import { computed, ref, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { useVoiceCallStatus } from 'dashboard/composables/useVoiceCallStatus';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import KaptionCampaignIndicator from './KaptionCampaignIndicator.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';

// NOTICE: KAPTION-INTEGRATION

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  // NOTICE: KAPTION-INTEGRATION - Preserve the agent filter in detail URLs.
  assigneeId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
  // NOTICE: KAPTION-INTEGRATION - Optional search match preview for single-pane conversation search.
  searchMatch: { type: Object, default: null },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
  'pinConversation',
  'unpinConversation',
]);

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const hovered = ref(false);
const showContextMenu = ref(false);

// NOTICE: KAPTION-INTEGRATION - Privacy mode (blur card content, hover to reveal)
const kaptionPrivacyEnabled = inject('kaptionPrivacyEnabled', ref(false));
const privacyHovering = ref(false);
const isBlurred = computed(
  () => kaptionPrivacyEnabled.value && !privacyHovering.value
);
const contextMenu = ref({
  x: null,
  y: null,
});

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

// NOTICE: KAPTION-INTEGRATION - Get team from store
const team = computed(() => {
  const teamId = props.chat.meta?.team?.id || props.chat.team_id;
  return teamId ? store.getters['teams/getTeamById'](teamId) : null;
});

const hasTeam = computed(() => !!team.value);

// NOTICE: KAPTION-INTEGRATION - Helper to detect if assignee is an assistant
const isAssigneeAssistant = computed(() => {
  const email = assignee.value?.email;
  if (!email) return false;
  return /@assistants\.kaptionai\.com$/i.test(email);
});

const assigneeIcon = computed(() => {
  return isAssigneeAssistant.value ? 'bot' : 'person';
});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  if (!senderId.value) return {};
  const fromStore = store.getters['contacts/getContact'](senderId.value);
  if (fromStore?.name) return fromStore;
  const sender = chatMetadata.value.sender;
  return sender?.name ? sender : fromStore || {};
});

// NOTICE: KAPTION-INTEGRATION - Add phone number computed property
const currentContactPhoneNumber = computed(() => {
  // Try to get phone number from conversation meta first, then from store
  // Note: phone number is snake_case in the data structure
  return (
    chatMetadata.value.sender?.phone_number ||
    currentContact.value?.phone_number ||
    ''
  );
});

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);

const isInboxNameVisible = computed(() => !activeInbox.value);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const callStatus = computed(
  () => props.chat.additional_attributes?.call_status
);
const callDirection = computed(
  () => props.chat.additional_attributes?.call_direction
);

const { labelKey: voiceLabelKey, listIconColor: voiceIconColor } =
  useVoiceCallStatus(callStatus, callDirection);

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const showInboxName = computed(() => {
  return (
    !props.hideInboxName &&
    isInboxNameVisible.value &&
    inboxesList.value.length > 1
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// NOTICE: KAPTION-INTEGRATION - Check if campaign exists for showMetaSection
const hasCampaign = computed(() => {
  return !!(props.chat?.meta?.campaign?.title || props.chat?.campaign?.title);
});

const isMuted = computed(() => !!props.chat.muted);

const isPinned = computed(() => !!props.chat.pinned);

const showMetaSection = computed(() => {
  return (
    showInboxName.value ||
    (props.showAssignee && assignee.value.name) ||
    hasTeam.value ||
    props.chat.priority ||
    hasCampaign.value ||
    isMuted.value ||
    isPinned.value
  );
});

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const searchMatchIsContact = computed(() => {
  if (!props.searchMatch) return false;
  return [
    'contact_name',
    'contact_email',
    'contact_phone',
    'contact_identifier',
    'contact_company_name',
  ].includes(props.searchMatch.source_field);
});

const highlightedContactName = computed(() => {
  if (!searchMatchIsContact.value || !currentContact.value?.name) return '';
  const name = currentContact.value.name;
  const terms = (
    props.searchMatch.snippet?.match(/<mark>(.*?)<\/mark>/g) || []
  ).map(m => m.replace(/<\/?mark>/g, ''));
  if (terms.length === 0) return name;
  let result = name;
  terms.forEach(term => {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(
      new RegExp(`(${escaped})`, 'gi'),
      '<mark>$1</mark>'
    );
  });
  return result;
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      assigneeId: props.assigneeId,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

// NOTICE: KAPTION-INTEGRATION
const onAssignAssistant = assistant => {
  emit('assignAssistant', assistant, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
  closeContextMenu();
};

const onAssignTeam = selectedTeam => {
  emit('assignTeam', selectedTeam, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};

const toggleMute = muteArg => {
  const conversationId = props.chat.id;
  if (isMuted.value || muteArg === null) {
    // Unmute (muteArg is null from unmute MenuItem)
    store.dispatch('unmuteConversation', conversationId);
    useAlert(t('CONVERSATION.RESOLVE_DROPDOWN.UNMUTE_SUCCESS'));
    closeContextMenu();
  }
  // Mute is handled by ninja-keys picker via context menu method
};

const togglePin = () => {
  const conversationId = props.chat.id;
  if (isPinned.value) {
    store.dispatch('unpinConversation', { conversationId });
    useAlert(t('CONVERSATION.RESOLVE_DROPDOWN.UNPIN_SUCCESS'));
    emit('unpinConversation', conversationId);
  } else {
    store.dispatch('pinConversation', { conversationId });
    useAlert(t('CONVERSATION.RESOLVE_DROPDOWN.PIN_SUCCESS'));
    emit('pinConversation', conversationId);
  }
  closeContextMenu();
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 border-t-0 border-b-0 border-l-0 border-r-0 border-transparent border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :class="{
      'active animate-card-select bg-n-alpha-1 dark:bg-n-alpha-3 border-n-weak':
        isActiveChat,
      'bg-n-slate-2 dark:bg-n-slate-3': selected,
      'bg-n-teal-3 dark:bg-n-teal-4': hasUnread && !isActiveChat && !selected,
      'px-0': compact,
      'px-3': !compact,
      'kaption-privacy-blur': isBlurred,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
    @mouseenter="privacyHovering = true"
    @mouseleave="privacyHovering = false"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        :class="!showInboxName ? 'mt-4' : 'mt-8'"
        hide-offline-status
        rounded-full
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
    </div>
    <div
      class="px-0 py-3 border-b group-hover:border-transparent flex-1 border-n-slate-3 min-w-0"
    >
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-center gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center truncate"
          >
            <!-- NOTICE: KAPTION-INTEGRATION - Show emoji icon for assistants, person icon for agents -->
            <fluent-icon
              :icon="assigneeIcon"
              size="12"
              class="text-n-slate-11"
            />
            {{ assignee.name }}
          </span>
          <!-- NOTICE: KAPTION-INTEGRATION - Team indicator -->
          <span
            v-if="hasTeam"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center truncate"
          >
            <fluent-icon
              icon="people-team"
              size="12"
              class="text-kpt-purple mr-0.5"
            />
            {{ team.name }}
          </span>
          <!-- NOTICE: KAPTION-INTEGRATION - Campaign indicator component -->
          <KaptionCampaignIndicator :chat="chat" class="flex-shrink-0" />
          <PriorityMark :priority="chat.priority" class="flex-shrink-0" />
          <span
            v-if="isMuted"
            v-tooltip="{
              content: $t('CONVERSATION.RESOLVE_DROPDOWN.MUTED_INDICATOR'),
              delay: { show: 1500, hide: 0 },
              hideOnClick: true,
            }"
            class="shrink-0 rounded-sm inline-flex items-center justify-center w-3.5 h-3.5 bg-n-slate-4 text-n-slate-11"
          >
            <span class="i-lucide-volume-off size-3" />
          </span>
          <span
            v-if="isPinned"
            v-tooltip="{
              content: $t('CONVERSATION.CARD.PINNED'),
              delay: { show: 1500, hide: 0 },
              hideOnClick: true,
            }"
            class="shrink-0 rounded-sm inline-flex items-center justify-center w-3.5 h-3.5 bg-n-amber-3 text-n-amber-11"
            data-test-id="conversation-pinned-indicator"
          >
            <span class="i-lucide-pin size-3" />
          </span>
        </div>
      </div>
      <!-- NOTICE: KAPTION-INTEGRATION - Modified contact name display to include phone number -->
      <div class="flex flex-col my-0 mx-2 pt-0.5 min-w-0 ltr:pr-16 rtl:pl-16">
        <h4
          class="conversation--user text-sm capitalize text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ currentContact.name }}
        </h4>
        <p
          v-if="currentContactPhoneNumber"
          class="text-xs text-n-slate-10 text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 mt-0.5"
        >
          {{ currentContactPhoneNumber }}
        </p>
      </div>
      <div
        v-if="callStatus"
        key="voice-status-row"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <span
          class="inline-block -mt-0.5 align-middle text-[16px] i-ph-phone-incoming"
          :class="[voiceIconColor]"
        />
        <span class="mx-1">
          {{ $t(voiceLabelKey) }}
        </span>
      </div>
      <div
        v-else-if="searchMatch"
        key="search-match-preview"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <span
          v-if="searchMatchIsContact"
          v-dompurify-html="highlightedContactName"
          class="font-medium [&_mark]:bg-n-amber-4 [&_mark]:text-n-amber-12 [&_mark]:rounded-sm [&_mark]:px-0.5"
        />
        <span
          v-else
          v-dompurify-html="searchMatch.snippet"
          class="[&_mark]:bg-n-amber-4 [&_mark]:text-n-amber-12 [&_mark]:rounded-sm [&_mark]:px-0.5"
        />
      </div>
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
          />
        </span>
        <span
          class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 ltr:ml-auto rtl:mr-auto mt-1 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9"
          :class="hasUnread ? 'block' : 'hidden'"
        >
          {{
            unreadCount > 9 ? $t('CONVERSATION.UNREAD_COUNT_MANY') : unreadCount
          }}
        </span>
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :muted="isMuted"
        :pinned="isPinned"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-assistant="onAssignAssistant"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @mute-conversation="toggleMute"
        @toggle-pin="togglePin"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>

<style scoped>
/* NOTICE: KAPTION-INTEGRATION - Privacy mode blur (no transition — animating blur is expensive) */
.kaption-privacy-blur {
  filter: blur(6px);
  user-select: none;
}
</style>
